import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from '@/components/Link';
import { Button as UIButton } from './ui/button';

export type ButtonVariant = 'primary' | 'line' | 'ghost';
type ButtonSize = 'default' | 'icon';

type CommonProps = {
  variant?: ButtonVariant;
  /** `icon` renders a square, padding-free button for a single glyph */
  size?: ButtonSize;
  className?: string;
  children?: ReactNode;
};

type AsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { as?: 'button'; to?: undefined; href?: undefined };

type AsAnchor = CommonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { as: 'a'; href?: string; to?: string };

export type ButtonProps = AsButton | AsAnchor;

/**
 * Polymorphic button on top of shadcn's `Button` (cva + Radix Slot).
 * `as:'a'` renders an anchor - a router `<Link>` when `to` is set - via `asChild`.
 */
export const Button = ({
  variant = 'primary',
  size = 'default',
  className,
  children,
  ...rest
}: ButtonProps) => {
  if (rest.as === 'a') {
    const { as: _as, to, href, ...anchorRest } = rest;
    void _as;
    const anchor =
      to !== undefined ? (
        <Link to={to} {...anchorRest}>
          {children}
        </Link>
      ) : (
        <a href={href} {...anchorRest}>
          {children}
        </a>
      );
    return (
      <UIButton asChild variant={variant} size={size} className={className}>
        {anchor}
      </UIButton>
    );
  }

  const { as: _as, ...buttonRest } = rest;
  void _as;
  return (
    <UIButton variant={variant} size={size} className={className} {...buttonRest}>
      {children}
    </UIButton>
  );
};
